import {css} from "@emotion/react";
import React from 'react';
import {useCreateCallLogs, useDeleteCallLogs, useUpdateCallLogs} from "../../../../sdk/mutations";
import {useCallLogsById} from "../../../../sdk/queries";
import {Button} from '../../../Button';
import {Modal, ModalProps} from "../../../Modal";
import {columnModal, LineMark, rowModal} from "../../styles";
import {DataAction} from "../800CallLogsProvider";
import {MapRow} from "../MapRow";
import {SimpleRow, SimpleRowEditProps} from "../SimpleRow";
import {TextAreaModal} from "../TextAreaModal";
import {bigError, editDeleteContainer, errorSpan} from "./styles";
import moment from "moment";


export type Modal800CallLogsProps = {
    mode?: "view" | "edit" | "create";
    modalProps: ModalProps;
    callLogId?: number | null;
    onModify?(id: number, action: DataAction): void;
}

const editOrCreate: Array<NonNullable<Modal800CallLogsProps['mode']>> = ["edit", "create"];
const editOrView: Array<NonNullable<Modal800CallLogsProps['mode']>> = ["edit", "view"];
export type CallLogsInputsData = Partial<{
    calledDate: string;
    callTime: string;
    callerName: string;
    callerPhone: string;
    callerText: string;
    actionsTaken: string;

    //Address data
    callerZip: string;
    callerState: string;
    callerCity: string;
    callerAddress: string;
}>

const timeToMoment = (input: string) => {
    return moment(input.replace(/(( hours, )|( minutes and )|( seconds))/g, ":"),"HH:mm:ss:",true);
}

export const Modal800CallLogs: React.FC<Modal800CallLogsProps> = ({
    mode = "view",
    modalProps,
    callLogId,
    onModify,
}) => {
    const isEditOrCreate = editOrCreate.includes(mode);
    const isEditOrView = editOrView.includes(mode);

    const [submitLoading, setSubmitLoading] = React.useState<boolean>(false);

    React.useLayoutEffect(() => {
        setData({});
        setSubmitLoading(false);
    }, [modalProps.open]);

    const [data, setData] = React.useState<CallLogsInputsData>({});

    const [error, setError] = React.useState({errorState: "", errorTime: "", errorDate: "", errorFlag: 0});

    const strCallLogId = callLogId !== null && callLogId !== undefined ? String(callLogId) : "";

    const {data: callLogByIdData, loading: callLogByIdLoading} = useCallLogsById({
        skip: !modalProps.open || !isEditOrView || !strCallLogId,
        variables: {
            id: strCallLogId,
        },
        cachePolicy: "no-cache",
    });


    React.useLayoutEffect(() => {
        if (isEditOrView) {
            if (!callLogByIdLoading) {
                const _data = callLogByIdData?.data;
                setData({
                    calledDate: _data?.calledAt?.split("T")[0],
                    callTime: _data?.calledAt?.split("T")[1],
                    callerName: _data?.callerName,
                    callerPhone: _data?.callerPhone,
                    callerText: _data?.callerText,
                    actionsTaken: _data?.actionsTaken,
                    callerZip: _data?.callerZip,
                    callerState: _data?.callerState,
                    callerCity: _data?.callerCity,
                    callerAddress: _data?.callerAddress,
                })
                if (!!callLogByIdData?.errors.length) {
                    console.error("[Modal800CallLogs] ", callLogByIdData?.errors, "for ID", callLogId);
                }
            }
        }
    }, [callLogByIdData, callLogByIdLoading]);


    const isGettingData = callLogByIdLoading && isEditOrView;

    const [createCallLog, {data: createCallLogData, loading: createCallLogLoading}] = useCreateCallLogs();
    const [updateCallLog, {data: updateCallLogData, loading: updateCallLogLoading}] = useUpdateCallLogs();
    const [deleteCallLog, {data: deleteCallLogData, loading: deleteCallLogLoading}] = useDeleteCallLogs();


    const _onDataChange = (name: string, value: string) => {
        setData(prev => ({...prev, [name]: value}));
    }

    const editProps = (name: keyof CallLogsInputsData): SimpleRowEditProps | undefined => {

        const isDate = name.toLowerCase().includes("date");
        const isTime = name.toLowerCase().includes("time");

        return isEditOrCreate ? {
            name,
            value: data[name],
            onChange: _onDataChange,
            placeholder: isDate ? "e.g: 2020-03-29" : isTime ? "e.g: 18hh:03mm:58ss" : undefined,
        } : undefined;
    }

    const onDelete = () => {
        setSubmitLoading(true);
        deleteCallLog({
            variables: {
                id: String(callLogId),
            },
        }).then(x => {
            setSubmitLoading(false);
            if(typeof callLogId === "number") {
                onModify?.(callLogId, "delete");
                modalProps.onClose?.();
            }
        });
    }

    const isCorrect = () => {
        if(handleValidation()){
            onSubmit();
        }else{
            console.log("form has errors")
        }
    }

    interface errorTypes {
        errorState: string,
        errorTime: string,
        errorDate: string,
        errorFlag: number
    }

    const handleValidation = () => {
        let formIsValid = true;
        let obj: errorTypes = {errorState: "", errorTime: "", errorDate: "", errorFlag: 0};

        if(!data.callerState){
            formIsValid = false;
            obj["errorState"] = "*Required";
            obj["errorFlag"] = 1;
        }

        if(!data.calledDate){
            formIsValid = false;
            obj["errorDate"] = "*Required";
            obj["errorFlag"] = 1;
        }

        if(!data.callTime){
            formIsValid = false;
            obj["errorTime"] = "*Required";
            obj["errorFlag"] = 1;
        }
        console.log(data?.callTime)
        if(!moment(data?.callTime,"HH:mm:ss").isValid()){
            formIsValid =  false;
            obj["errorTime"] = "*Invalid time format";
            obj["errorFlag"] = 1;
        }

        setError(obj);
        console.log(obj);
        return formIsValid;
    }




    const onSubmit = () => {
        setSubmitLoading(true);
        // Choose whether to use edit or create mutation

        const chosenMutation = mode === "edit" ? updateCallLog : createCallLog;
        chosenMutation({
            data: {
                actionsTaken: data.actionsTaken || "",
                calledAt: data.calledDate + " " + data.callTime || "",
                callerAddress: data.callerAddress || "",
                callerCity: data.callerCity || "",
                callerName: data.callerName || "",
                callerPhone: data.callerPhone || "",
                callerState: data.callerState || "",
                callerText: data.callerText || "",
                callerZip: data.callerZip || "",
            },
            variables: mode === "edit" ? {
                id: String(callLogId),
            } : undefined,
        }).then(x => {
            setSubmitLoading(false);
            const id = x?.data?.id ?? null;
            if (id !== null) {
                console.debug("CREATED", id);
                onModify?.(id, "create");
                modalProps.onClose?.();
            }
        });
    };

    return (
        <Modal title={mode === "edit" ? "Edit Call" :
            mode === "create" ? "Create Call" : "Call Details"
        } {...modalProps}>
            <div>
                <div css={columnModal}>
                    {
                        isGettingData ? (
                            "Getting details..."
                        ) : (
                            <React.Fragment>
                                <div css={rowModal} style={{display:"flex",alignContent:"center"}}>

                                    <SimpleRow fullWidth={false}
                                               editProps={editProps("calledDate")} label={"Call Date"}
                                               multiRow={true}
                                               variant={"date"}
                                               value={data.calledDate}/>
                                    {mode === "view" ? <LineMark/> : ""}
                                    <span css={errorSpan}>{error.errorDate}</span>

                                    <SimpleRow fullWidth={false} multiRow={true} variant = "time" editProps={editProps("callTime")} label={"Call Time"} value={data.callTime}/>
                                    <span css={errorSpan}>{error.errorTime}</span>
                                    <LineMark/>
                                    <SimpleRow fullWidth={false} multiRow={true} editProps={editProps("callerName")} label={"Caller's Name"}
                                               value={data.callerName}/>
                                    {mode === "view" ? <LineMark/> : ""}
                                    <SimpleRow fullWidth={false} multiRow={true} variant={"phone"} editProps={editProps("callerPhone")} label={"Caller's Contact #"} value={data.callerPhone}/>
                                </div>
                                <MapRow
                                    label={"Location"}
                                    values={{
                                        callerAddress: data.callerAddress || "",
                                        callerCity: data.callerCity || "",
                                        callerState: data.callerState || "",
                                        callerZip: data.callerZip || "",
                                    }}
                                    error = {error.errorState}
                                    editProps={isEditOrCreate ? {
                                        onChange: _onDataChange,
                                        values: {
                                            callerZip: data.callerZip || "",
                                            callerState: data.callerState || "",
                                            callerCity: data.callerCity || "",
                                            callerAddress: data.callerAddress || "",
                                        },

                                    } : undefined}
                                />
                                <TextAreaModal editProps={editProps("callerText")} label={"Complaint Content"}
                                                 value={data.callerText}/>

                                {mode === "view" ? <LineMark/> : <div style={{marginTop:10,marginBottom:10}}/>}

                                <TextAreaModal editProps={editProps("actionsTaken")} label={"Actions Taken"}
                                           value={data.actionsTaken}/>
                                <p css={bigError}>{error.errorFlag ? "ERROR: Could not create record, missing required fields." : ""}</p>
                                {mode === "view" ? <LineMark/> : ""}
                                {/*{!isEditOrCreate && <SimpleRow label={"Call File"} value={"File"}/>}*/}
                                {isEditOrCreate && <div css={editDeleteContainer}>

                                    {mode === "edit" &&

                                    <Button disabled={submitLoading}
                                            onClick={onDelete}
                                            overrideCss={css`background-color: #ef3838; margin-right:10px`}>Delete</Button>}

                                    <Button disabled={submitLoading}
                                            onClick={isCorrect}>{mode === "create" ? "Create" : "Edit"}</Button>

                                </div>}
                            </React.Fragment>
                        )
                    }

                </div>
            </div>
        </Modal>
    );
};